import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import useContacts from '#shared/hooks/useApi/useContacts';
import usePaymentMethods from '#shared/hooks/useApi/usePaymentMethods';

type Options = {
  // Sent as PortalUser on delete/set-default/add-Interac calls.
  loginUserName?: string;
  // True when a host injects its own instance into ContactDetailView — the
  // internal copy then stays inert (no mount fetches).
  skip?: boolean;
};

// The contact + payment methods a contact detail view renders. Each host
// (contact page, contact drawer) calls this once and passes the result into
// the detail components, so the page header and the tabs share one instance.
export default function useContactDetailData(
  contactID: string,
  clientAccountsEnabled: boolean,
  { loginUserName, skip }: Options = {},
) {
  const { contact, fetchContact } = useContacts();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Shared' });
  const alertDialog = useAlertDialog();

  const { groupedMethods, fetching, methods, fetchMethods, setDefaultMethod, deleteMethod, deleteGroupedMethod } =
    usePaymentMethods({
      accountID: contactID,
      currencies: ['CAD'],
      clientAccountsEnabled,
      loginUserName,
    });

  async function refreshPaymentMethods() {
    try {
      const res = await fetchMethods();

      return res;
    } catch (err) {
      const msg = err instanceof Error ? err.message : `Couldn't fetch payment methods for ${contactID}`;
      alertDialog.error(tGlobal('error', { defaultValue: 'Error' }), msg);
    }
  }

  async function refreshContact() {
    return fetchContact(contactID);
  }

  useEffect(() => {
    if (skip) return;

    fetchContact(contactID).catch(() => {
      // Logged by the hook; the view renders its empty state.
    });

    refreshPaymentMethods();
  }, []);

  return {
    contact,
    refreshContact,
    groupedMethods,
    fetching,
    methods,
    setDefaultMethod,
    deleteMethod,
    deleteGroupedMethod,
    refreshPaymentMethods,
  };
}

export type ContactDetailData = ReturnType<typeof useContactDetailData>;
